/**
 * Organizational partnership inquiry contracts (public lead + platform review).
 */
import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';

export const PARTNERSHIP_KINDS = Object.freeze([
  'corporate_service',
  'strategic_partner',
  'other',
] as const);
export type PartnershipKind = (typeof PARTNERSHIP_KINDS)[number];

export const ORGANIZATION_TYPES = Object.freeze([
  'company',
  'organization',
  'hotel',
  'complex',
  'government',
  'other',
] as const);
export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

export const PARTNERSHIP_INQUIRY_STATUSES = Object.freeze([
  'new',
  'contacting',
  'qualified',
  'closed',
  'spam',
] as const);
export type PartnershipInquiryStatus = (typeof PARTNERSHIP_INQUIRY_STATUSES)[number];

export const PARTNERSHIP_ROUTES = Object.freeze({
  submit: `${GHALISHOR_API_V1_PREFIX}/partnership-inquiries`,
  list: `${GHALISHOR_API_V1_PREFIX}/platform/partnership-inquiries`,
  update: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/partnership-inquiries/${encodeURIComponent(id)}`,
} as const);

export interface SubmitPartnershipInquiryRequest {
  readonly partnershipKind: PartnershipKind;
  readonly organizationName: string;
  readonly organizationType: OrganizationType;
  readonly contactFullName: string;
  readonly mobile: string;
  readonly email?: string | null;
  readonly city: string;
  readonly approximateMonthlyVolume?: number | null;
  readonly message: string;
  readonly preferredContactTime?: string | null;
}

export interface SubmitPartnershipInquiryResponse {
  readonly id: UuidString;
  readonly trackingCode: string;
  readonly createdAt: IsoDateTimeString;
}

export interface PartnershipInquirySummary {
  readonly id: UuidString;
  readonly trackingCode: string;
  readonly partnershipKind: PartnershipKind;
  readonly organizationName: string;
  readonly organizationType: OrganizationType;
  readonly contactFullName: string;
  readonly mobileE164: string;
  readonly email: string | null;
  readonly city: string;
  readonly approximateMonthlyVolume: number | null;
  readonly message: string;
  readonly preferredContactTime: string | null;
  readonly status: PartnershipInquiryStatus;
  readonly reviewerNote: string | null;
  readonly reviewedAt: IsoDateTimeString | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface ListPartnershipInquiriesResponse {
  readonly items: readonly PartnershipInquirySummary[];
}

export interface UpdatePartnershipInquiryRequest {
  readonly status: PartnershipInquiryStatus;
  readonly reviewerNote?: string | null;
}

export function isPartnershipKind(value: unknown): value is PartnershipKind {
  return typeof value === 'string' && (PARTNERSHIP_KINDS as readonly string[]).includes(value);
}

export function isOrganizationType(value: unknown): value is OrganizationType {
  return typeof value === 'string' && (ORGANIZATION_TYPES as readonly string[]).includes(value);
}

export function isPartnershipInquiryStatus(value: unknown): value is PartnershipInquiryStatus {
  return (
    typeof value === 'string' &&
    (PARTNERSHIP_INQUIRY_STATUSES as readonly string[]).includes(value)
  );
}

export function partnershipTrackingCode(id: string): string {
  return id.replace(/-/g, '').slice(0, 8).toUpperCase();
}
